import { useCallback, useMemo } from "react";

import { useNavigate, useParams } from "react-router-dom";

import { PencilIcon } from "@lucide/icons";
import { useQuery } from "@tanstack/react-query";

import { initUseQuery } from "simplydo/core/query";
import { RichText } from "simplydo/react/richtext";
import { Button } from "simplydo/react/ui/button";
import { Container, type ContainerProps } from "simplydo/react/ui/container";
import { Divider } from "simplydo/react/ui/divider";
import { H1 } from "simplydo/react/ui/header";
import { Icon } from "simplydo/react/ui/icon";
import {
  Message,
  MessageContent,
  MessageHeader,
} from "simplydo/react/ui/message";
import { Placeholder, PlaceholderLine } from "simplydo/react/ui/placeholder";
import { Segment } from "simplydo/react/ui/segment";

import api from "api";
import { useAppSelector } from "store";
import util from "utils/utils";

import Login from "components/entry/Login";

type CustomPageProps = {
  pageId?: string;
  isPreview?: boolean;
  containerProps?: ContainerProps;
};

const CustomPage = ({
  pageId: propPageId,
  isPreview = false,
  containerProps = { style: { paddingTop: 20, paddingBottom: 20 } },
}: CustomPageProps) => {
  const urlOrganisation = useAppSelector(
    (state) => state.organisations.urlOrganisation,
  );
  const user = useAppSelector((state) => state.user);
  const navigate = useNavigate();

  const { pageId: urlPageId } = useParams<{ pageId: string }>();
  const pageId = useMemo(
    () => propPageId ?? urlPageId,
    [propPageId, urlPageId],
  );

  const pageQuery = useQuery({
    ...initUseQuery(api.pages.get, [pageId ?? ""]),
    enabled: !!pageId,
  });
  const page = pageQuery.data?.page;

  const navigateToEditPage = useCallback(
    (id: string) => {
      if (!page) return;
      let path = "";
      if (page.forType === "group") {
        path = `/groups/${page.forId}/settings/pages/edit/${id}`;
      } else if (page.forType === "organisation") {
        path = `/admin/communication/pages/edit/${id}`;
      }
      if (path) navigate(path);
    },
    [navigate, page],
  );

  const canEditPage = useMemo(
    () => !!page && util.hasPermission(user, "page.edit", page._id, page.forId),
    [user, page],
  );

  if (pageQuery.isError && !user) {
    return (
      <Container {...containerProps}>
        <Segment
          style={{
            maxWidth: 700,
            width: 700,
            alignSelf: "center",
            padding: 50,
          }}
        >
          <Message
            color="blue"
            style={{ alignSelf: "center", marginBottom: 20 }}
          >
            This content is restricted to registered users, please log in to
            view this page.
          </Message>
          {/* @ts-ignore */}
          <Login embedded />
        </Segment>
      </Container>
    );
  }
  if (!page) return null;
  if (!user && !urlOrganisation) return null;

  return (
    <Container {...containerProps}>
      {!pageQuery.isLoading && pageQuery.isError ? (
        <Message color="red">
          <MessageHeader>You are unable to access this page</MessageHeader>
          <MessageContent>
            This page has been restricted to certain viewers only. Please
            contact an admin if you think this is in error
          </MessageContent>
        </Message>
      ) : (
        <>
          {pageQuery.isLoading ? (
            <Placeholder style={{ marginBottom: 20 }}>
              <PlaceholderLine length="medium" />
            </Placeholder>
          ) : (
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <H1>{page?.title ?? "New page"}</H1>
              {!isPreview && canEditPage ? (
                <Button
                  size="small"
                  onClick={() => navigateToEditPage(page._id)}
                  style={{ marginLeft: "auto" }}
                >
                  <Icon icon={PencilIcon} />
                  Edit page
                </Button>
              ) : null}
            </div>
          )}

          <Divider />

          {pageQuery.isLoading ? (
            <Placeholder style={{ marginBottom: 30 }}>
              <PlaceholderLine length="medium" />
              <PlaceholderLine length="medium" />
              <PlaceholderLine length="medium" />
              <PlaceholderLine length="medium" />
              <PlaceholderLine length="medium" />
            </Placeholder>
          ) : (
            <RichText
              readOnly
              value={page?.content ?? ""}
              forType="organisation"
              forId={user ? user.organisation : urlOrganisation!._id}
            />
          )}
        </>
      )}
    </Container>
  );
};

export default CustomPage;
